export interface ModuleMasteryRowLayoutInput {
  readonly expanded: boolean;
  readonly detailContentHeight: number;
  readonly hasEffectDetails: boolean;
  readonly hasBranchOptions?: boolean;
  readonly hasSelectedBranch?: boolean;
}

export interface ModuleMasteryRowLayout {
  readonly top: number;
  readonly height: number;
  readonly detailTop: number;
  readonly detailBottom: number;
  readonly expandButtonVisible: boolean;
}

export interface ModuleMasteryBranchOptionIconAlphaInput {
  readonly showBranchOptions: boolean;
  readonly optionIndex: number;
  readonly optionCount: number;
  readonly ready: boolean;
}

export interface ModuleMasterySelectedDetailIconAlphaInput {
  readonly expanded: boolean;
  readonly hasIcon: boolean;
}

export interface ModuleMasterySelectedDetailIconYInput {
  readonly rowTop: number;
  readonly detailTopOffset: number;
  readonly lineIndex: number;
}

export const MODULE_MASTERY_ROW_HEIGHT = 108;
export const MODULE_MASTERY_SELECTED_BRANCH_ROW_HEIGHT = 124;
export const MODULE_MASTERY_PROGRESS_CENTER_Y = 36;
export const MODULE_MASTERY_PROGRESS_BAR_WIDTH = 150;
export const MODULE_MASTERY_PROGRESS_BAR_HEIGHT = 7;
export const MODULE_MASTERY_PROGRESS_PERCENT_GAP = 8;
export const MODULE_MASTERY_PROGRESS_PERCENT_LABEL_WIDTH = 34;
export const MODULE_MASTERY_DETAIL_TOP = 116;
export const MODULE_MASTERY_BRANCH_OPTIONS_ROW_HEIGHT = 216;
export const MODULE_MASTERY_BRANCH_OPTIONS_DETAIL_TOP = 194;
export const MODULE_MASTERY_DETAIL_LINE_H = 20;
export const MODULE_MASTERY_DETAIL_TEXT_LINE_H = 13;
export const MODULE_MASTERY_DETAIL_ICON_CENTER_OFFSET_Y = 6;
export const MODULE_MASTERY_DETAIL_PAD_BOTTOM = 18;
export const MODULE_MASTERY_EXPANDED_ROW_MIN_HEIGHT =
  MODULE_MASTERY_DETAIL_TOP + MODULE_MASTERY_DETAIL_LINE_H * 3 + MODULE_MASTERY_DETAIL_PAD_BOTTOM;

export function shouldShowModuleMasteryExpandButton(hasEffectDetails: boolean): boolean {
  return hasEffectDetails;
}

export function moduleMasteryBranchOptionIconAlpha(input: ModuleMasteryBranchOptionIconAlphaInput): number {
  if (!input.showBranchOptions || input.optionIndex >= input.optionCount) return 0;
  return input.ready ? 1 : 0.45;
}

export function moduleMasterySelectedDetailIconAlpha(input: ModuleMasterySelectedDetailIconAlphaInput): number {
  return input.expanded && input.hasIcon ? 1 : 0;
}

export function moduleMasterySelectedDetailIconY(input: ModuleMasterySelectedDetailIconYInput): number {
  const lineIndex = Math.max(0, input.lineIndex);
  return (
    input.rowTop +
    input.detailTopOffset +
    lineIndex * MODULE_MASTERY_DETAIL_TEXT_LINE_H +
    MODULE_MASTERY_DETAIL_ICON_CENTER_OFFSET_Y
  );
}

export function computeModuleMasteryRowHeight(input: ModuleMasteryRowLayoutInput): number {
  const baseHeight = input.hasBranchOptions
    ? MODULE_MASTERY_BRANCH_OPTIONS_ROW_HEIGHT
    : input.hasSelectedBranch
      ? MODULE_MASTERY_SELECTED_BRANCH_ROW_HEIGHT
      : MODULE_MASTERY_ROW_HEIGHT;
  if (!input.expanded || !input.hasEffectDetails) return baseHeight;
  const detailTop = input.hasBranchOptions ? MODULE_MASTERY_BRANCH_OPTIONS_DETAIL_TOP : MODULE_MASTERY_DETAIL_TOP;
  const detailHeight = Math.max(0, Math.ceil(input.detailContentHeight));
  return Math.max(
    input.hasBranchOptions
      ? MODULE_MASTERY_BRANCH_OPTIONS_DETAIL_TOP + MODULE_MASTERY_DETAIL_LINE_H * 3 + MODULE_MASTERY_DETAIL_PAD_BOTTOM
      : MODULE_MASTERY_EXPANDED_ROW_MIN_HEIGHT,
    detailTop + detailHeight + MODULE_MASTERY_DETAIL_PAD_BOTTOM,
  );
}

export function computeModuleMasteryRowLayouts(
  rows: readonly ModuleMasteryRowLayoutInput[],
): { readonly rows: readonly ModuleMasteryRowLayout[]; readonly contentHeight: number } {
  let top = 0;
  const layouts = rows.map((row) => {
    const height = computeModuleMasteryRowHeight(row);
    const expandButtonVisible = shouldShowModuleMasteryExpandButton(row.hasEffectDetails);
    const detailTop =
      top + (row.hasBranchOptions ? MODULE_MASTERY_BRANCH_OPTIONS_DETAIL_TOP : MODULE_MASTERY_DETAIL_TOP);
    const detailBottom =
      row.expanded && row.hasEffectDetails
        ? detailTop + Math.max(0, Math.ceil(row.detailContentHeight))
        : detailTop;
    const layout: ModuleMasteryRowLayout = {
      top,
      height,
      detailTop,
      detailBottom,
      expandButtonVisible,
    };
    top += height;
    return layout;
  });
  return { rows: layouts, contentHeight: top };
}
